// src/components/UnresolvedAppointmentsGate.jsx
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useAuth } from "../auth/AuthProvider";
import Icon from "./AppIcon";

/**
 * "Poarta" programărilor trecute fără status.
 *
 * Cere /b/appointments/unresolved la fiecare schimbare de pagină, la revenirea în tab și
 * după orice modificare de programări. Dacă există programări încheiate de peste 2 ore
 * (începând cu 21.10.2026) lăsate 'booked', afișează o fereastră care NU se poate închide
 * până nu sunt toate marcate Finalizat / Anulat. Doar superadminul o poate închide, pentru
 * că vede business-urile altora.
 */
export default function UnresolvedAppointmentsGate() {
  const { currentBusinessId, permissions, user } = useAuth();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [errors, setErrors] = useState({});
  const [dismissed, setDismissed] = useState(false);

  const bid = currentBusinessId ? String(currentBusinessId) : "";

  const load = useCallback(async () => {
    if (!bid || !user?.id) return;
    try {
      const { data } = await api.get("/b/appointments/unresolved", {
        headers: { "X-Business-Id": bid },
        params: { limit: 100 },
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setCount(Number(data?.count || 0) || 0);
    } catch {
      // fără acces / fără feature: nu blocăm nimic
      setItems([]);
      setCount(0);
    }
  }, [bid, user?.id]);

  useEffect(() => {
    load();
  }, [load, location.pathname]);

  useEffect(() => {
    const onRefetch = () => load();
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("appointments:refetch", onRefetch);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("appointments:refetch", onRefetch);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  const resolve = async (appt, status) => {
    if (!bid || !appt?.id) return;
    setBusyId(appt.id);
    setErrors((e) => ({ ...e, [appt.id]: "" }));
    try {
      await api.patch(`/b/appointments/${appt.id}`, { status }, { headers: { "X-Business-Id": bid } });
      setItems((prev) => prev.filter((x) => x.id !== appt.id));
      setCount((c) => Math.max(0, c - 1));
      try {
        window.dispatchEvent(new CustomEvent("notifications:new"));
      } catch {}
    } catch (err) {
      const d = err?.response?.data;
      let msg = "";
      if (d && typeof d === "object") {
        msg =
          (typeof d.detail === "string" && d.detail) ||
          Object.values(d)
            .flat()
            .filter((v) => typeof v === "string")
            .join(" ");
      }
      setErrors((e) => ({ ...e, [appt.id]: msg || t("unresolved.error") }));
    } finally {
      setBusyId(null);
    }
  };

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      const lang = (i18n.language || "ro").split("-")[0];
      const locale = lang === "ro" ? "ro-RO" : lang;
      return d.toLocaleString(locale, {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  if (!bid || count <= 0) return null;
  const isSuper = !!permissions?.isSuperAdmin;
  if (isSuper && dismissed) return null;
  if (typeof document === "undefined") return null;

  const hidden = Math.max(0, count - items.length);

  return createPortal(
    <div
      className="fixed inset-0 z-[400] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full sm:max-w-2xl bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col max-h-[90dvh]">
        <div className="px-4 sm:px-6 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-base sm:text-lg font-semibold text-foreground">
              <Icon name="Clock" size={20} className="text-amber-500 shrink-0" />
              <span>{t("unresolved.title")}</span>
            </div>
            <div className="text-sm text-muted-foreground mt-1">{t("unresolved.subtitle", { count })}</div>
          </div>
          {isSuper ? (
            <button
              type="button"
              className="p-2 rounded-md hover:bg-muted"
              onClick={() => setDismissed(true)}
              aria-label={t("common.close")}
            >
              <Icon name="X" size={18} />
            </button>
          ) : null}
        </div>

        <div className="px-4 sm:px-6 py-4 overflow-y-auto">
          <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
            {items.map((a) => {
              const services =
                Array.isArray(a.service_names) && a.service_names.length
                  ? a.service_names.join(", ")
                  : a.service_name || "";
              const busy = busyId === a.id;
              return (
                <div key={a.id} className="px-4 py-3 bg-card">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-foreground">{fmt(a.start_at)}</div>
                      <div className="text-sm text-muted-foreground break-words">
                        {services}
                        {a.client_name ? ` · ${a.client_name}` : ""}
                        {a.staff_name ? ` · ${a.staff_name}` : ""}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => resolve(a, "completed")}
                        className="text-xs px-3 py-2 rounded-md bg-green-500 hover:bg-green-600 text-white font-medium disabled:opacity-50"
                      >
                        {t("unresolved.completed")}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => resolve(a, "cancelled")}
                        className="text-xs px-3 py-2 rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground font-medium disabled:opacity-50"
                      >
                        {t("unresolved.cancelled")}
                      </button>
                    </div>
                  </div>
                  {errors[a.id] ? <div className="mt-1 text-xs text-destructive">{errors[a.id]}</div> : null}
                </div>
              );
            })}
          </div>
          {hidden > 0 ? (
            <div className="mt-3 text-xs text-muted-foreground">{t("unresolved.more", { count: hidden })}</div>
          ) : null}
        </div>

        <div className="px-4 sm:px-6 py-3 border-t border-border text-xs text-muted-foreground">
          {t("unresolved.hint")}
        </div>
      </div>
    </div>,
    document.body
  );
}
